import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../api';
import { ErrorState, Loading, useTitle } from '../ui/bits';
import { Icon, type IconName } from '../ui/Icon';
import { Fig, PageHead } from '../ui/viz';
import { useToast } from '../ui/toast';
import { count, peso } from '../lib/format.js';
import { SETTINGS_SPEC, describeField, isDirty, toDraft, validateSettings, type FieldSpec, type SettingKey } from '../lib/settings.js';
import { opsKeys, useOpsSettings } from './queries';
import { FeeLadder } from './zones/FeeLadder';
import './zones/setup.css';

type Draft = Record<string, string | boolean>;
type Drafts = Record<SettingKey, Draft>;
type Errors = Partial<Record<SettingKey, Record<string, string>>>;
type Server = Record<SettingKey, Record<string, unknown>>;

const KEYS: SettingKey[] = ['delivery', 'express', 'cod', 'dispatch', 'grill', 'mart', 'handoff', 'marketplace', 'riders'];

/** How each group sits on the page: a short name, an icon, and its width on the 12-column grid. */
const LOOK: Record<SettingKey, { name: string; icon: IconName; span: number }> = {
  delivery: { name: 'Delivery', icon: 'pin', span: 12 },
  express: { name: 'Express', icon: 'bolt', span: 12 },
  cod: { name: 'Cash on delivery', icon: 'cash', span: 6 },
  dispatch: { name: 'Dispatch', icon: 'swap', span: 6 },
  grill: { name: 'Grill kitchen', icon: 'flame', span: 4 },
  mart: { name: 'Grocery', icon: 'bag', span: 4 },
  handoff: { name: 'Ready for rider', icon: 'box', span: 4 },
  marketplace: { name: 'Partner shops', icon: 'pie', span: 6 },
  riders: { name: 'Riders', icon: 'person', span: 6 },
};

/** Two-to-three-word names for the live figures (the spec's labels are whole sentences). */
const SHORT: Record<string, string> = {
  baseFeeCentavos: 'Starts at',
  includedKm: 'Included',
  perKmCentavos: 'Each extra km',
  maxKm: 'Up to',
  clusterRadiusKm: 'Shared rider',
  extraStopCentavos: 'Extra pickup',
  enabled: 'Express',
  surchargeCentavos: 'Surcharge',
  promiseMinutes: 'Promise',
  lateCreditCentavos: 'Late credit',
  maxActive: 'At once',
  escalateAfterSeconds: 'Flag after',
  riskMarginMinutes: 'At risk under',
  creditExpiryDays: 'Credit lasts',
  riderChangeLimitCentavos: 'Change float',
  riderCashLimitCentavos: 'Remit above',
  assignBeforeReadyMinutes: 'Book ahead',
  preAssignMinutes: 'Pre-assign',
  lowStockThreshold: 'Few left at',
  staleAfterMinutes: 'Stuck after',
  defaultCommissionBps: 'Commission',
  requireCompleteProfile: 'Full profile',
};

function draftsFrom(server: Server): Drafts {
  return {
    delivery: toDraft('delivery', server.delivery),
    express: toDraft('express', server.express),
    cod: toDraft('cod', server.cod),
    dispatch: toDraft('dispatch', server.dispatch),
    grill: toDraft('grill', server.grill), // older servers may not send it: the field shows the default (5 min)
    mart: toDraft('mart', server.mart), // likewise: the threshold shows the default (5)
    handoff: toDraft('handoff', server.handoff), // likewise: shows the default (240 min)
    marketplace: toDraft('marketplace', server.marketplace), // ops people: 15% until the server sends it
    riders: toDraft('riders', server.riders), // ops people: on until the server sends it
  };
}

/** A wire value as a figure string: "₱49", "2 km", "30 min", "15%", "120 s", "30 days". */
function figOf(f: FieldSpec, raw: unknown): string {
  let v = raw;
  if ((v === undefined || v === null) && typeof f.default === 'number') v = f.default;
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  switch (f.kind) {
    case 'peso':
      return peso(v);
    case 'percent':
      return `${Math.round(v) / 100}%`;
    case 'km':
      return `${v} km`;
    case 'minutes':
      return `${v} min`;
    case 'seconds':
      return `${v} s`;
    case 'days':
      return `${v} ${v === 1 ? 'day' : 'days'}`;
    default:
      return count(v);
  }
}

function isOn(f: FieldSpec, v: unknown) {
  return v === undefined || v === null ? f.defaultOn === true : v === true;
}

export function SettingsPage() {
  useTitle('Settings');
  const settings = useOpsSettings();
  const qc = useQueryClient();
  const toast = useToast();
  const [drafts, setDrafts] = useState<Drafts | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const server = settings.data as Server | undefined;
  useEffect(() => {
    if (server && !drafts) setDrafts(draftsFrom(server));
  }, [server, drafts]);

  if (settings.isError)
    return (
      <>
        <Header />
        <section className="card">
          <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
        </section>
      </>
    );
  if (!server || !drafts)
    return (
      <>
        <Header />
        <Loading label="Loading settings" rows={3} />
      </>
    );

  const dirtyKeys = KEYS.filter((k) => isDirty(k, drafts[k], server[k]));

  const setField = (key: SettingKey, name: string, v: string | boolean) => {
    setDrafts((d) => (d ? { ...d, [key]: { ...d[key], [name]: v } } : d));
    setErrors((e) => {
      const ke = e[key];
      if (!ke || !ke[name]) return e;
      const { [name]: _drop, ...rest } = ke;
      return { ...e, [key]: rest };
    });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirtyKeys.length) return;
    const nextErrors: Errors = {};
    const values: Partial<Record<SettingKey, Record<string, unknown>>> = {};
    for (const k of dirtyKeys) {
      const r = validateSettings(k, drafts[k], server[k]);
      if (r.ok) values[k] = r.value;
      else nextErrors[k] = r.errors;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      // Move focus to the first problem so keyboard and screen-reader users land on it.
      window.setTimeout(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 0);
      toast('Some values need fixing before saving.', 'error');
      return;
    }
    setSaving(true);
    const saved: SettingKey[] = [];
    try {
      for (const k of dirtyKeys) {
        const v = values[k];
        if (!v) continue;
        // The core client's key type predates 'handoff' (product gaps); the route takes any known key.
        const fresh = await api.putOpsSetting(k as Parameters<typeof api.putOpsSetting>[0], v);
        qc.setQueryData<Server>(opsKeys.settings, (old) => (old ? { ...old, [k]: fresh ?? v } : old));
        saved.push(k);
      }
      const next = qc.getQueryData<Server>(opsKeys.settings);
      if (next) setDrafts(draftsFrom(next));
      toast('Settings saved. New orders use them right away.');
    } catch (err) {
      toast(`${saved.length ? 'Some settings saved, but ' : ''}${errorMessage(err, "couldn't save.")}`, 'error');
      void qc.invalidateQueries({ queryKey: opsKeys.settings });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={save} noValidate className="stack s16 set-page">
      <Header />
      <div className="dv-grid set-grid">
        {SETTINGS_SPEC.map((section, i) => {
          const d = drafts[section.key];
          const errs = errors[section.key] ?? {};
          const check = validateSettings(section.key, d, server[section.key]);
          const preview = check.ok ? check.value : (server[section.key] ?? {});
          const look = LOOK[section.key];
          const dirty = dirtyKeys.includes(section.key);
          const bad = Object.values(errs).some(Boolean);
          const wide = section.fields.length > 2;
          return (
            <section
              key={section.key}
              id={`sec-${section.key}`}
              className={`card dv-card dv-rise set-sec${wide ? ' wide' : ''}${dirty ? ' dirty' : ''}`}
              style={{ gridColumn: `span ${look.span}`, ['--i' as string]: i }}
              aria-labelledby={`set-${section.key}`}
            >
              <div className="dv-card-h">
                <span className="set-ic" aria-hidden="true">
                  <Icon name={look.icon} />
                </span>
                <h2 id={`set-${section.key}`} title={section.intro}>
                  {look.name}
                  <span className="sr-only"> · {section.title}</span>
                </h2>
                {bad ? <span className="dv-chip bad">Fix</span> : dirty ? <span className="dv-chip set-edited">Edited</span> : null}
              </div>
              <div className={wide ? 'set-body wide' : 'set-body'}>
                <div className="set-preview" aria-live="polite">
                  {section.fields.map((f) => (
                    <div key={f.name} className="set-fig" title={describeField(f, preview[f.name])}>
                      <span className="dv-label">{SHORT[f.name] ?? f.label}</span>
                      {f.kind === 'bool' ? (
                        <span className={isOn(f, preview[f.name]) ? 'dv-chip good set-bool' : 'dv-chip set-bool'}>{isOn(f, preview[f.name]) ? 'On' : 'Off'}</span>
                      ) : (
                        <Fig text={figOf(f, preview[f.name])} size="md" />
                      )}
                      <span className="sr-only">{describeField(f, preview[f.name])}</span>
                    </div>
                  ))}
                </div>
                {section.key === 'delivery' ? (
                  <div className="set-ladder">
                    <span className="dv-label">Fee by distance</span>
                    <FeeLadder delivery={preview} compact />
                  </div>
                ) : null}
                <div className="set-fields">
                  {section.fields.map((f) => (
                    <Field key={f.name} id={`${section.key}-${f.name}`} spec={f} value={d[f.name] ?? ''} error={errs[f.name]} onChange={(v) => setField(section.key, f.name, v)} />
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>
      <div className={dirtyKeys.length ? 'set-bar on' : 'set-bar'}>
        <span className="set-bar-n" aria-live="polite">
          {dirtyKeys.length ? (
            <>
              <Fig text={String(dirtyKeys.length)} size="sm" animate={false} />
              <span>
                Unsaved in {dirtyKeys.map((k) => LOOK[k].name).join(', ')}
              </span>
            </>
          ) : (
            <>
              <Icon name="check" />
              <span>All changes saved</span>
            </>
          )}
        </span>
        <span className="grow" />
        <button
          type="button"
          className="btn btn-quiet"
          disabled={!dirtyKeys.length || saving}
          onClick={() => {
            setDrafts(draftsFrom(server));
            setErrors({});
          }}
        >
          Discard changes
        </button>
        <button type="submit" className="btn btn-primary" disabled={!dirtyKeys.length || saving} aria-busy={saving || undefined}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

function Header() {
  return <PageHead title="Settings" meta="Applies to new orders right away" />;
}

function Field({ id, spec, value, error, onChange }: { id: string; spec: FieldSpec; value: string | boolean; error?: string; onChange(v: string | boolean): void }) {
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  const describedBy = [spec.help ? helpId : null, error ? errId : null].filter(Boolean).join(' ') || undefined;
  // The help sentence stays for screen readers (aria-describedby) and rides on a "?" tooltip.
  const help = spec.help ? (
    <span id={helpId} className="sr-only">
      {spec.help}
    </span>
  ) : null;
  const tip = spec.help ? (
    <span className="set-tip" title={spec.help} aria-hidden="true">
      ?
    </span>
  ) : null;
  if (spec.kind === 'bool') {
    return (
      <div className="field set-field">
        <button type="button" role="switch" id={id} className="switch" aria-checked={value === true} aria-describedby={describedBy} onClick={() => onChange(value !== true)}>
          <span className="track" aria-hidden="true" />
          <span className="t-label">{spec.label}</span>
          {tip}
        </button>
        {help}
      </div>
    );
  }
  const prefix = spec.kind === 'peso' ? '₱' : null;
  const suffix = spec.kind === 'percent' ? '%' : spec.kind === 'km' ? 'km' : spec.kind === 'minutes' ? 'min' : spec.kind === 'seconds' ? 'sec' : spec.kind === 'days' ? 'days' : null;
  return (
    <div className="field set-field">
      <label htmlFor={id}>
        {spec.label}
        {tip}
      </label>
      <div className="input-affix" data-invalid={error ? 'true' : undefined}>
        {prefix ? <span aria-hidden="true" className="t-mono">{prefix}</span> : null}
        <input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        />
        {suffix ? <span aria-hidden="true" className="t-mono muted">{suffix}</span> : null}
      </div>
      {help}
      {error ? (
        <span id={errId} className="pill st-error err-pill" style={{ whiteSpace: 'normal' }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
